import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface UsageTotals {
    apiCalls: number
    mcpCalls: number
    webhookDeliveries: number
    agentRuns: number
    agentTokens: number
}

export interface MonthlyUsage extends UsageTotals {
    month: string
}

export interface WorkspaceUsage extends UsageTotals {
    workspaceId: string
    name: string
}

export interface PlanBreakdown {
    free: number
    active: number
    canceling: number
    canceled: number
}

export interface DashboardMetrics {
    workspaces: { total: number; new30d: number; active7: number; active30: number; byPlan: PlanBreakdown }
    users: { total: number }
    projects: { total: number }
    tasks: { total: number; completed: number; new30d: number }
    agent: { runs: number; tokens: number }
}

export interface AdminMetrics {
    dashboard: DashboardMetrics
    apiMcp: {
        totals: UsageTotals
        monthly: MonthlyUsage[]
        topByApi: WorkspaceUsage[]
        topByMcp: WorkspaceUsage[]
    }
}

interface AdminState {
    metrics: AdminMetrics | null
    loading: boolean
    error: string | null
    // The signed-in user isn't an admin (callable returned permission-denied). Kept
    // separate from `error` so the page can render a clean "not authorized" state.
    forbidden: boolean
}

const initialState: AdminState = {
    metrics: null,
    loading: false,
    error: null,
    forbidden: false,
}

const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        // Optional trailing-months window for the API/MCP section (undefined = all time).
        fetchAdminMetricsSaga: (_state, _action: PayloadAction<number | undefined>) => {},

        setAdminLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setAdminMetrics: (state, action: PayloadAction<AdminMetrics>) => {
            state.metrics = action.payload
            state.error = null
            state.forbidden = false
        },
        setAdminError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        setAdminForbidden: (state, action: PayloadAction<boolean>) => {
            state.forbidden = action.payload
        },
    },
})

export const {
    fetchAdminMetricsSaga,
    setAdminLoading,
    setAdminMetrics,
    setAdminError,
    setAdminForbidden,
} = adminSlice.actions

export default adminSlice.reducer
